import React, {Component} from "react";
import {Button, Col, Container, Form, Row} from "react-bootstrap";
import DirectBillingReferralTypeEnum from "../../../enums/DirectBillingReferralTypeEnum";
import CptCodeSelector from "../../component/CptCodeSelector";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import ValidationUtil from "../../../../../components/form/validator/ValidationUtil";
import EnumInsuranceReferralStatus from "../../referral/enums/EnumInsuranceReferralStatus";
import PatientSearchModal from "../../../../appointment/modals/PatientSearchModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import PatientUtil from "../../../../../utils/PatientUtil";
import DateUtil from "../../../../../utils/DateUtil";
import {FormField} from "../../../../../components/form";
import {DateInput} from "../../../../../components";
import CreateDiagnosisGroupModal from "./CreateDiagnosisGroupModal";
import EnumDirectBillingType from "../../direct/EnumDirectBillingType";
import Select from "react-select";
import ExternalClinicianSelect from "../../component/ExternalClinicianSelect";
import VisitSelectionModal from "./VisitSelectionModal";

export default class CreateReferralModal extends Component {

    constructor(props) {
        super(props);

        this.state = {
            referral: {
                patientId: null,
                cptList: [],
                speciality: null,
                clinicId: "",
                referenceNo: "",
                validFrom: null,
                validUntil: null,
                noOfRequestedSessions: null,
                coPayMax: null,
                diagnosisGroup: null,
                referringClinicianName: "",
                referringClinicianLicenseNo: "",
            },
            referralType: DirectBillingReferralTypeEnum.EXTERNAL,
            sendToOptions: [],
            clinicOptions: [],
            selectedPatient: null,
            patientSearchVisible: false,
            diagnosisGroupModalVisible: false,
            visitSearchVisible: false,
            visitList: [],
            selectedVisit: null,
            insuranceReferralStatus: EnumInsuranceReferralStatus.toArray()
        }
    }

    componentDidMount() {
        this.retrieveSendToOptions();
        this.retrieveClinicOptions();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevState.referralType !== this.state.referralType) {
            this.setState({referral: {}, selectedVisit: null})
        }
    }

    static createExternalReferral = (referral) => {
        return RemotingService.postRemoteCall(
            'api/insurance/coordinator/v2/referral/external',
            referral,
            (item) => {
                return item
            })
    }

    static createInternalReferral = (referral) => {
        return RemotingService.postRemoteCall(
            'api/insurance/coordinator/v2/referral/internal',
            referral,
            (item) => {
                return item
            })
    }

    saveReferral = () => {
        if (!ValidationUtil.checkWithNotification(this.formFields)) {
            return;
        }
        // Prepare the referral data
        const referralData = {
            ...this.state.referral,
            patientId: this.state.selectedPatient?.id
        };

        const onSuccess = () => {
            if (this.props.onClose) {
                this.props.onClose();
            }
        };

        if (this.state.referralType === DirectBillingReferralTypeEnum.EXTERNAL) {
            RemotingService.postRemoteCall('api/insurance/coordinator/v2/referral/external',
                referralData, onSuccess)
        } else {
            RemotingService.postRemoteCall('api/insurance/coordinator/v2/referral/internal',
                {...referralData, visitId: this.state.selectedVisit?.id}, onSuccess)
        }
    }

    retrieveSendToOptions = () => {
        RemotingService.getRemoteCall('api/staff/list-base', null, (result) => {
            const staffs = result.items;
            staffs.forEach(i => {
                i.key = "" + i.id;
            });
            const sendToOptions = [
                {name: 'Insurance', key: 'INSURANCE'},
                {name: 'Patient', key: 'PATIENT'},
                ...staffs,
                {name: 'Other', key: 'OTHER'}];
            this.setState({sendToOptions});
        });
    }

    retrieveClinicOptions = () => {
        RemotingService.getRemoteCall('api/clinic/list', null, (result) => {
            const clinics = result.items;
            clinics.forEach(i => {
                i.key = "" + i.id;
            });
            this.setState({clinicOptions: clinics});
        });
    }

    retrieveVisitList = (patientId) => {
        RemotingService.getRemoteCall('/api/visit_history/patient/' + patientId, {staffTypes: 'DOCTOR'}, (result) => {
            this.setState({visitList: result})
        })
    }

    referralTypeTemplate = () => {
        // Format options for react-select
        const options = DirectBillingReferralTypeEnum.toArray().map(option => ({
            value: option.key,
            label: option.name
        }));
        
        // Custom styling similar to specialityTemplate
        const customStyles = {
            control: (base) => ({
                ...base,
                width: '100%',
                minHeight: '30px',
                height: '35px',
                fontSize: '14px',
                padding: '0px',
            }),
            valueContainer: (base) => ({

                ...base,
                height: '30px',
                padding: '0 8px'
            }),
            input: (base) => ({
                ...base,
                margin: '0px',
                padding: '0px'
            }),
            indicatorsContainer: (base) => ({
                ...base,
                height: '30px',
            }),
            option: (base) => ({
                ...base,
                fontSize: '14px',
                padding: '6px 8px'
            }),
        };
        
        // Find the current value in options
        const selectedOption = options.find(option => option.value === this.state.referralType.key);
        
        return (
            <Select
                options={options}
                value={selectedOption}
                onChange={(selectedOption) => {
                    this.setState({referralType: DirectBillingReferralTypeEnum[selectedOption.value]})
                }}
                styles={customStyles}
                placeholder="Select Referral Type"
            />
        )
    }

    cptCodesTemplate = () => {
        return <CptCodeSelector cptList={this.state.referral.cptList}
                                referralType={this.state.referral.speciality}
                                patientId={this.state.selectedPatient?.id}
                                onCptSelected={(cptList) => {
                                    this.setState({referral: { ...this.state.referral, cptList: cptList }})
                                }}/>;
    }

    specialityTemplate = () => {
        let optionsSource;
        
        // Add if check to set different options based on referral type
        if (this.state.referralType === DirectBillingReferralTypeEnum.EXTERNAL) {
            // For external referrals, use the same typeOptions as in InsuranceReferralModal
            optionsSource = EnumDirectBillingType.directPaySelectableTypes;
        } else {
            // For internal referrals, currently using the same options
            // In future, this will be different for internal referrals
            optionsSource = EnumDirectBillingType.directPaySelectableTypes;
        }
        
        // Format options for react-select
        const options = optionsSource.map(option => ({
            value: option.key,
            label: option.name
        }));
        
        // Custom styling similar to PreApproval.js
        const customStyles = {
            control: (base) => ({
                ...base,
                width: '100%',
                minHeight: '30px',
                height: '35px',
                fontSize: '14px',
                padding: '0px',
            }),
            valueContainer: (base) => ({
                ...base,
                height: '30px',
                padding: '0 8px'
            }),
            input: (base) => ({
                ...base,
                margin: '0px',
                padding: '0px'
            }),
            indicatorsContainer: (base) => ({
                ...base,
                height: '30px',
            }),
            option: (base) => ({
                ...base,
                fontSize: '14px',
                padding: '6px 8px'
            }),
        };
        
        // Find the current value in options
        const selectedOption = options.find(option => option.value === this.state.referral.speciality);

        return (
            <Select
                options={options}
                value={selectedOption}
                onChange={(selectedOption) => {
                    this.setState({
                        referral: { 
                            ...this.state.referral, 
                            speciality: selectedOption.value 
                        }
                    });
                }}
                styles={customStyles}
                placeholder="Select Speciality"
            />
        );
    }

    clinicTemplate = () => {
        const options = this.state.clinicOptions.map(option => ({
            value: option.id,
            label: option.name
        }));

        return (<Select
            options={options}
            value={options.find(option => option.value === this.state.referral.clinicId)}
            onChange={(selectedOption) => {
                this.setState({
                    referral: {
                        ...this.state.referral,
                        clinicId: selectedOption.value
                    }
                });
            }}
        ></Select>);
    }

    refNoTemplate = () => {
        return <Form.Control
            type="text"
            placeholder="reference no"
            style={{ fontSize: '14px', padding: '10px', borderRadius: '8px' }}
            value={this.state.referral.referenceNo || ''}
            onChange={(e) => {
                this.setState({referral: { ...this.state.referral, referenceNo: e.target.value }})
            }}
        />
    }

    dateInputTemplate = (field) => {
        return (
            <DateInput className={"whiteBackground"} value={this.state.referral[field]}
                       onChange={(value) => this.setState({referral: { ...this.state.referral, [field]: value }})}/>
        )
    }

    sessionsTemplate = () => {
        return <Form.Control
            type="number"
            placeholder="Number of sessions"
            style={{ fontSize: '14px', padding: '10px', borderRadius: '8px' }}
            value={this.state.referral.noOfRequestedSessions || ''}
            onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value) : null;
                this.setState({referral: { ...this.state.referral, noOfRequestedSessions: value }})
            }}
        />
    }

    maxCoPayTemplate = () => {
        return <Form.Control
            type="number"
            step="0.01"
            placeholder="Maximum co-pay"
            style={{ fontSize: '14px', padding: '10px', borderRadius: '8px' }}
            value={this.state.referral.coPayMax || ''}
            onChange={(e) => {
                const value = e.target.value ? parseFloat(e.target.value) : null;
                this.setState({referral: { ...this.state.referral, coPayMax: value }})
            }}
        />
    }

    handleClinicianSelect = (clinician) => {
        this.setState({referral: {
            ...this.state.referral,
            referringClinicianName: clinician.name,
            referringClinicianLicenseNo: clinician.licenseNo
        }});
    }

    referredByTemplate = () => {
        return <ExternalClinicianSelect
            field="name"
            placeholder="Referring Clinician Name"
            value={this.state.referral.referringClinicianName}
            onTextChange={(text) => this.setState({referral: { ...this.state.referral, referringClinicianName: text }})}
            onClinicianSelect={this.handleClinicianSelect}
        />
    }

    licenseNoTemplate = () => {
        return <ExternalClinicianSelect
            field="licenseNo"
            placeholder="Referring Clinician License"
            value={this.state.referral.referringClinicianLicenseNo}
            onTextChange={(text) => this.setState({referral: { ...this.state.referral, referringClinicianLicenseNo: text }})}
            onClinicianSelect={this.handleClinicianSelect}
        />
    }

    openVisitSearch = () => {
        this.setState({visitSearchVisible: true});
    }

    closeVisitSearch = () => {
        this.setState({visitSearchVisible: false});
    }

    handleSelectedVisit = (visit) => {
        this.setState({selectedVisit: visit, visitSearchVisible: false});
    }

    clearSelectedVisit = () => {
        this.setState({selectedVisit: null});
    }

    renderVisitSelectButton = () => {
        return (
            this.state.selectedVisit ?
                <div className="display-flex flex-row justify-content-between">
                    {this.state.selectedVisit.date ? DateUtil.formatDateTime12H(this.state.selectedVisit.date) : `Visit #${this.state.selectedVisit.id}`}
                    <div style={{color: "red", cursor: "pointer", textAlign: "right"}}
                         onClick={this.clearSelectedVisit}>
                        ⓧ
                    </div>
                </div> :
                <FormField ref={formField => this.formFields.push(formField)} required
                           validateOn={this.state.selectedVisit}>
                    <Button variant={"outline-secondary w-100"}
                            disabled={!this.state.selectedPatient}
                            onClick={this.openVisitSearch}
                            size={"sm"}>
                        <FontAwesomeIcon icon={["fas", "calendar-alt"]}/>
                        Select Visit
                    </Button>
                </FormField>
        )
    }

    renderVisitSearch = () => {
        return this.state.visitSearchVisible &&
            <VisitSelectionModal
                header={"Select Visit"}
                visitList={this.state.visitList}
                onSelect={this.handleSelectedVisit}
                close={this.closeVisitSearch}
            />;
    }

    openPatientSearch = () => {
        ValidationUtil.clear(this.formFields);
        this.setState({patientSearchVisible: true});
    }

    closePatientSearch = () => {
        this.setState({patientSearchVisible: false});
    }

    handleSelectedPatient = (patient) => {
        this.setState({selectedPatient: patient, patientSearchVisible: false, selectedVisit: null, visitList: []});
        if (!patient.insurance) {
            this.setState({insuranceReferralStatus: this.state.insuranceReferralStatus.filter(status => status !== EnumInsuranceReferralStatus.APPROVED)});
        } else {
            this.setState({insuranceReferralStatus: EnumInsuranceReferralStatus.toArray()});
        }
        this.retrieveVisitList(patient.id);
    }

    clearSelectedPatient = () => {
        this.setState({selectedPatient: null, selectedVisit: null, visitList: []});
    }
    
    openDiagnosisGroupModal = () => {
        this.setState({diagnosisGroupModalVisible: true});
    }
    
    closeDiagnosisGroupModal = () => {
        this.setState({diagnosisGroupModalVisible: false});
    }
    
    handleSaveDiagnosisGroup = (diagnosisGroup) => {
        this.setState({
            referral: { ...this.state.referral, diagnosisGroup: diagnosisGroup },
            diagnosisGroupModalVisible: false
        });
    }

    renderPatientSearchButton = () => {
        return (
            this.state.selectedPatient ?
                <div className="display-flex flex-row justify-content-between">
                    {PatientUtil.getPatientName(this.state.selectedPatient.firstName, this.state.selectedPatient.middleName, this.state.selectedPatient.lastName)}
                    <div style={{color: "red", cursor: "pointer", textAlign: "right"}}
                         onClick={this.clearSelectedPatient}>
                        ⓧ
                    </div>
                </div> :
                <FormField ref={formField => this.formFields.push(formField)} required
                           validateOn={this.state.selectedPatient}>
                    <Button variant={"outline-secondary w-100"}
                            onClick={this.openPatientSearch}
                            size={"sm"}>
                        <FontAwesomeIcon icon={["fas", "search"]}/>
                        Search Patients
                    </Button>
                </FormField>
        )
    }

    renderPatientSearch = () => {
        return this.state.patientSearchVisible &&
            <PatientSearchModal
                header={"Find a Patient"}
                selectIcon={<FontAwesomeIcon icon={["fas", "user-plus"]} size={"2x"} color={"#b1d93f"}
                                             style={{marginTop: "3px"}}/>}
                handleFindPatientModalSelect={this.handleSelectedPatient}
                customInsuranceExcluded={true}
                close={this.closePatientSearch}
            />;
    }
    
    renderDiagnosisGroupButton = () => {
        const { diagnosisGroup } = this.state.referral;
        
        return (
            <div>
                {diagnosisGroup ? (
                    <div className="display-flex flex-row justify-content-between">
                        <div>
                            {diagnosisGroup.diagnosisItems?.length || 0} Diagnosis Item(s)
                        </div>
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={this.openDiagnosisGroupModal}
                            style={{ marginLeft: "10px" }}
                        >
                            <FontAwesomeIcon icon={["fas", "edit"]} /> Edit
                        </Button>
                    </div>
                ) : (
                    <Button 
                        variant="outline-secondary w-100" 
                        onClick={this.openDiagnosisGroupModal}
                        size="sm"
                    >
                        <FontAwesomeIcon icon={["fas", "plus"]} /> Add Diagnosis
                    </Button>
                )}
            </div>
        );
    }
    
    renderDiagnosisGroupModal = () => {
        return this.state.diagnosisGroupModalVisible && (
            <CreateDiagnosisGroupModal
                diagnosisGroup={this.state.referral.diagnosisGroup}
                onSave={this.handleSaveDiagnosisGroup}
                onCancel={this.closeDiagnosisGroupModal}
            />
        );
    }

    renderInternalReferral = () => {
        return this.renderReferralForm(true);
    }

    renderExternalReferral = () => {
        return this.renderReferralForm(false);
    }

    renderReferralForm = (isInternal) => {
        return (
            <div>
                <Container>
                    <Row>
                        <Col>
                            <Container>
                                <Field required label="Patient" body={this.renderPatientSearchButton()}/>
                            </Container>
                        </Col>
                        <Col>
                            <Container>
                                {isInternal
                                    ? <Field required label="Visit" body={this.renderVisitSelectButton()}/>
                                    : <Field required label="Diagnosis" body={
                                        <FormField ref={formField => this.formFields.push(formField)} required
                                                   validateOn={this.state.referral.diagnosisGroup}>
                                            {this.renderDiagnosisGroupButton()}
                                        </FormField>
                                    }/>}
                            </Container>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Container>
                                <Field required label="Clinic" body={
                                    <FormField ref={formField => this.formFields.push(formField)} required
                                               validateOn={this.state.referral.clinicId}>
                                        {this.clinicTemplate()}
                                    </FormField>
                                } />
                            </Container>
                        </Col>
                        <Col>
                            <Container>
                                <Field required label="Speciality" body={
                                    <FormField ref={formField => this.formFields.push(formField)} required
                                               validateOn={this.state.referral.speciality}>
                                        {this.specialityTemplate()}
                                    </FormField>
                                } />
                            </Container>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Container>
                                <Field label="Valid From" body={this.dateInputTemplate('validFrom')} />
                            </Container>
                        </Col>
                        <Col>
                            <Container>
                                <Field label="Valid Until" body={this.dateInputTemplate('validUntil')} />
                            </Container>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Container>
                                <Field label="Reference No" body={this.refNoTemplate()} />
                            </Container>
                        </Col>
                        <Col>
                            <Container>
                                <Field required label="Requested Sessions" body={
                                    <FormField ref={formField => this.formFields.push(formField)} required
                                               validateOn={this.state.referral.noOfRequestedSessions}>
                                        {this.sessionsTemplate()}
                                    </FormField>
                                } />
                            </Container>
                        </Col>
                    </Row>
                    {!isInternal &&
                        <Row>
                            <Col>
                                <Container>
                                    <Field required label="Referring Clinician Name" body={
                                        <FormField ref={formField => this.formFields.push(formField)} required
                                                   validateOn={this.state.referral.referringClinicianName}>
                                            {this.referredByTemplate()}
                                        </FormField>
                                    } />
                                </Container>
                            </Col>
                            <Col>
                                <Container>
                                    <Field required label="Referring Clinician License" body={
                                        <FormField ref={formField => this.formFields.push(formField)} required
                                                   validateOn={this.state.referral.referringClinicianLicenseNo}>
                                            {this.licenseNoTemplate()}
                                        </FormField>
                                    } />
                                </Container>
                            </Col>
                        </Row>}
                    <Row>
                        <Col>
                            <Container>
                                <Field label="Max Co-Pay" body={this.maxCoPayTemplate()} />
                            </Container>
                        </Col>
                        <Col>
                            <Container>
                                <Field required label="CPT Codes" body={
                                    <FormField ref={formField => this.formFields.push(formField)} required
                                               validateOn={this.state.referral.cptList?.length}>
                                        {this.cptCodesTemplate()}
                                    </FormField>
                                } />
                            </Container>
                        </Col>
                    </Row>
                </Container>
            </div>
        )
    }

    render() {
        this.formFields = []
        return (
            <>
                <div>
                    <Container className={"InputModal"} style={{width: 800, height: 50}}>
                        <Row>
                            <Col>
                                {this.referralTypeTemplate()}
                            </Col>
                        </Row>
                    </Container>
                    <Container className={"InputModal"} style={{width: 800, height: 600}}>
                        {this.state.referralType === DirectBillingReferralTypeEnum.INTERNAL ? this.renderInternalReferral() : this.renderExternalReferral()}
                    </Container>
                    <Container style={{width: 800, marginTop: 10, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button 
                            variant="primary" 
                            onClick={this.saveReferral}
                            style={{marginRight: 10}}
                        >
                            Save
                        </Button>
                        {this.props.onClose && (
                            <Button 
                                variant="secondary" 
                                onClick={this.props.onClose}
                            >
                                Cancel
                            </Button>
                        )}
                    </Container>
                </div>
                {this.renderPatientSearch()}
                {this.renderDiagnosisGroupModal()}
                {this.renderVisitSearch()}
            </>
        )
    }
}

class Field extends Component {
    render() {
        const {label, body, required} = this.props;
        return <div style={{display: 'grid', marginBottom: 8}}>
            <div><b>{label}{required && <span className="text-danger"> *</span>}</b></div>
            {body}
        </div>
    }
}